import type { QueryClient } from "@tanstack/react-query";
import { RivetsApiError, SNAPSHOT_GONE_CODE, diffStatFromSummary, type DiffStat, type RivetsClient, type SnapshotDiffScope, type SnapshotFileSummary, type SnapshotSummary, type Worktree, type WorktreeDetailResponse } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";

/**
 * Snapshot summaries (K3) and what a new one invalidates.
 *
 * The summary names every changed file with the hash of its diff, so two
 * summaries say exactly which files moved between them: a path in only one of
 * them, or in both with a different `diffHash`. Only those files, and the
 * directories above them, are refetched. Everything else in the file browser
 * keeps its cached listing and content (`staleTime: Infinity`).
 */

/** The list the Changes panel reviews, and the diff scope its rows fetch. */
export function reviewChanges(summary: SnapshotSummary | null | undefined): { changes: SnapshotFileSummary[]; scope: SnapshotDiffScope } {
  if (summary?.branchChanges) return { changes: summary.branchChanges, scope: "branch" };
  return { changes: summary?.changes ?? [], scope: "changes" };
}

function entryKey(scope: SnapshotDiffScope, path: string) {
  return `${scope}\u0000${path}`;
}

function indexEntries(summary: SnapshotSummary | null | undefined): Map<string, SnapshotFileSummary> {
  const index = new Map<string, SnapshotFileSummary>();
  for (const change of summary?.changes ?? []) index.set(entryKey("changes", change.path), change);
  for (const change of summary?.branchChanges ?? []) index.set(entryKey("branch", change.path), change);
  return index;
}

/** Whether an entry's diff hash identifies its content: it has a served diff,
 * and that diff is the whole of it. */
function provesUnchanged(change: SnapshotFileSummary): boolean {
  return change.diffHash !== "" && change.diffTruncated !== true;
}

/** Paths whose content may differ between two summaries: in either changed
 * set but not both, or in both with a different diff, plus rename sources. */
export function changedPathsBetween(previous: SnapshotSummary | null | undefined, next: SnapshotSummary | null | undefined): Set<string> {
  const before = indexEntries(previous);
  const after = indexEntries(next);
  const paths = new Set<string>();
  const note = (change: SnapshotFileSummary) => {
    paths.add(change.path);
    if (change.oldPath) paths.add(change.oldPath);
  };
  for (const [key, change] of after) {
    const old = before.get(key);
    // `diffHash` names the *served* diff. When there is none (binary, or
    // dropped by the upload budget) or it was capped, equal hashes say
    // nothing about the file's content, so such a path always counts.
    if (!old || !provesUnchanged(old) || !provesUnchanged(change)
      || old.diffHash !== change.diffHash || old.diffBytes !== change.diffBytes || old.status !== change.status
      || old.additions !== change.additions || old.deletions !== change.deletions) {
      note(change);
      if (old) note(old);
    }
  }
  for (const [key, change] of before) if (!after.has(key)) note(change);
  return paths;
}

/** Every directory above a path, the worktree root (`""`) included. */
export function parentDirectories(path: string): string[] {
  const parts = path.split("/");
  const dirs = [""];
  for (let index = 1; index < parts.length; index++) dirs.push(parts.slice(0, index).join("/"));
  return dirs;
}

/** Expires the cached file content and directory listings a set of changed
 * paths can have touched. Only queries on screen refetch. One pass over the
 * cache: a call per path scanned the whole cache each time, about a second for
 * a 500-file diff over a 2,000-query cache. */
export function invalidateChangedPaths(queryClient: QueryClient, worktreeId: string, paths: Iterable<string>): void {
  const files = new Set<string>();
  const dirs = new Set<string>();
  for (const path of paths) {
    files.add(path);
    // A path that is itself a directory (a new untracked folder) lists anew.
    dirs.add(path);
    for (const dir of parentDirectories(path)) dirs.add(dir);
  }
  if (files.size === 0) return;
  const [fileRoot] = qk.file(worktreeId, "");
  const [dirRoot] = qk.dir(worktreeId, "");
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (key.length !== 3 || key[1] !== worktreeId || typeof key[2] !== "string") return false;
      return key[0] === fileRoot ? files.has(key[2]) : key[0] === dirRoot ? dirs.has(key[2]) : false;
    },
  });
}

/** A summary moved from `previous` to `next`: expire what changed. */
export function applySummaryChange(
  queryClient: QueryClient,
  worktreeId: string,
  previous: SnapshotSummary | null | undefined,
  next: SnapshotSummary | null | undefined,
): void {
  invalidateChangedPaths(queryClient, worktreeId, changedPathsBetween(previous, next));
}

/**
 * Every change of a worktree's summary, whatever moved it — a relay
 * `snapshot.updated`, a LocalApi `worktree.changed`, the reconcile after a
 * reconnect that missed some, a Checks-panel refresh — expires exactly the
 * files and folders it changed. One watcher on the query cache, so no path
 * that refetches a summary can forget to.
 */
export function watchSummaryChanges(queryClient: QueryClient): () => void {
  const seen = new Map<string, SnapshotSummary | null>();
  return queryClient.getQueryCache().subscribe((event) => {
    const key = event.query.queryKey;
    if (key[0] !== "snapshot" || typeof key[1] !== "string") return;
    const worktreeId = key[1];
    if (event.type === "removed") {
      seen.delete(worktreeId);
      return;
    }
    const data = event.query.state.data as SnapshotSummary | null | undefined;
    if (event.type === "added") {
      if (data !== undefined) seen.set(worktreeId, data);
      return;
    }
    if (event.type !== "updated" || event.action.type !== "success" || data === undefined) return;
    const known = seen.has(worktreeId);
    const previous = seen.get(worktreeId);
    seen.set(worktreeId, data);
    if (!known || (previous?.id ?? null) === (data?.id ?? null)) return;
    applySummaryChange(queryClient, worktreeId, previous, data);
  });
}

function hasObservers(queryClient: QueryClient, worktreeId: string): boolean {
  return (queryClient.getQueryCache().find({ queryKey: qk.snapshot(worktreeId), exact: true })?.getObserversCount() ?? 0) > 0;
}

/**
 * A relay `snapshot.updated`. The totals ride on the frame, so the sidebar,
 * Home and the overview are patched without any fetch. The summary is only
 * refetched where something shows it; with nothing watching, every file and
 * directory of the worktree is simply marked stale (none of them is on screen,
 * so nothing is fetched).
 */
export async function applySnapshotUpdated(
  queryClient: QueryClient,
  update: { worktreeId: string; snapshotId: string; diffStat: DiffStat },
  options: { localWatching?: boolean } = {},
): Promise<void> {
  const { worktreeId, snapshotId, diffStat } = update;
  patchWorktreeDiffStat(queryClient, worktreeId, snapshotId, diffStat);
  // The daemon's own watch is fresher than any relay snapshot; it owns the
  // summary and the invalidations while it is up.
  if (options.localWatching) return;
  const previous = queryClient.getQueryData<SnapshotSummary | null>(qk.snapshot(worktreeId));
  if (previous === undefined || !hasObservers(queryClient, worktreeId)) {
    void queryClient.invalidateQueries({ queryKey: qk.snapshot(worktreeId), exact: true });
    void queryClient.invalidateQueries({ queryKey: qk.files(worktreeId) });
    void queryClient.invalidateQueries({ queryKey: qk.dirs(worktreeId) });
    return;
  }
  if (previous?.id === snapshotId) return;
  // The refetched summary reaches `watchSummaryChanges`, which expires what changed.
  await queryClient.invalidateQueries({ queryKey: qk.snapshot(worktreeId), exact: true }, { cancelRefetch: false });
}

/** A LocalApi `worktree.changed`: the summary arrives with the frame. */
export function applyLocalSummary(queryClient: QueryClient, worktreeId: string, summary: SnapshotSummary): void {
  const previous = queryClient.getQueryData<SnapshotSummary | null>(qk.snapshot(worktreeId));
  if (previous?.id === summary.id) return;
  // `watchSummaryChanges` expires what changed.
  queryClient.setQueryData(qk.snapshot(worktreeId), summary);
}

/** `diffStat` and `snapshotId` on every cached copy of the worktree row. */
export function patchWorktreeDiffStat(queryClient: QueryClient, worktreeId: string, snapshotId: string, diffStat: DiffStat): void {
  const patch = (worktree: Worktree): Worktree => worktree.id === worktreeId ? { ...worktree, diffStat, snapshotId } : worktree;
  queryClient.setQueryData<WorktreeDetailResponse>(qk.worktree(worktreeId), (old) => old
    ? { ...old, latestSnapshotId: snapshotId, worktree: patch(old.worktree) }
    : old);
  for (const [key, data] of queryClient.getQueriesData<{ worktrees: Worktree[] }>({ queryKey: ["worktrees"] })) {
    if (!data?.worktrees.some((worktree) => worktree.id === worktreeId)) continue;
    queryClient.setQueryData(key, { ...data, worktrees: data.worktrees.map(patch) });
  }
}

// -- per-file diffs ------------------------------------------------------------

/** Diff text by `diffHash`: a file whose diff did not change between two
 * snapshots opens from memory in the new one too. Bounded by entries and by
 * total characters (a diff can be 200k characters). */
const diffTextByHash = new Map<string, string | null>();
const DIFF_TEXT_CACHE_ENTRIES = 200;
export const DIFF_TEXT_CACHE_CHARS = 4_000_000;
let diffTextChars = 0;

export function rememberDiffText(diffHash: string, diff: string | null): void {
  if (!diffHash) return;
  const previous = diffTextByHash.get(diffHash);
  if (previous !== undefined) diffTextChars -= previous?.length ?? 0;
  diffTextByHash.delete(diffHash);
  if ((diff?.length ?? 0) > DIFF_TEXT_CACHE_CHARS) return;
  diffTextByHash.set(diffHash, diff);
  diffTextChars += diff?.length ?? 0;
  while (diffTextByHash.size > DIFF_TEXT_CACHE_ENTRIES || diffTextChars > DIFF_TEXT_CACHE_CHARS) {
    const oldest = diffTextByHash.keys().next().value;
    if (oldest === undefined) break;
    diffTextChars -= diffTextByHash.get(oldest)?.length ?? 0;
    diffTextByHash.delete(oldest);
  }
}

/** Test seam. */
export function diffTextCacheSize(): { entries: number; chars: number } {
  return { entries: diffTextByHash.size, chars: diffTextChars };
}

export function knownDiffText(diffHash: string | undefined): string | null | undefined {
  if (!diffHash) return undefined;
  return diffTextByHash.get(diffHash);
}

// -- lazy path lists ------------------------------------------------------------

export function isSnapshotGone(error: unknown): boolean {
  return error instanceof RivetsApiError && (error.status === 410 || error.code === SNAPSHOT_GONE_CODE);
}

/**
 * A snapshot's path list, for resolving transcript file links, or null when
 * it cannot be had (the link then opens as written). Snapshots are pruned, so
 * the id a task row carries can be gone: on 410 the latest relay summary is
 * read, the task's snapshot id is moved to it (so the next click asks for the
 * right one), and the list is fetched once more.
 */
export async function loadSnapshotPaths(
  queryClient: QueryClient,
  worktreeId: string,
  snapshotId: string,
  api: Pick<RivetsClient, "snapshotPaths" | "worktreeSnapshotSummary"> = client,
): Promise<readonly string[] | null> {
  const fetchPaths = (id: string) => queryClient.fetchQuery({
    queryKey: qk.snapshotPaths(id),
    queryFn: () => api.snapshotPaths(id),
    staleTime: Infinity,
    retry: false,
  }).then((response) => response.paths);
  try {
    return await fetchPaths(snapshotId);
  } catch (error) {
    if (!isSnapshotGone(error)) return null;
  }
  const latest = await api.worktreeSnapshotSummary(worktreeId).catch(() => null);
  if (!latest || latest.id === snapshotId) return null;
  patchWorktreeDiffStat(queryClient, worktreeId, latest.id, diffStatFromSummary(latest));
  // The Changes panel was on the pruned snapshot too.
  void queryClient.invalidateQueries({ queryKey: qk.snapshot(worktreeId), exact: true });
  return fetchPaths(latest.id).catch(() => null);
}
